import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/base/ArticleCard";
import ScrollToTop from "@/components/base/ScrollToTop";
import { featuredArticles, articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const heroBg =
  "https://readdy.ai/api/search-image?query=Japanese%20actress%20Yu%20Aoi%20elegant%20portrait%20in%20soft%20warm%20light%2C%20minimalist%20Japanese%20aesthetic%20background%2C%20artistic%20blurred%20sakura%20petals%2C%20warm%20golden%20hour%20lighting%2C%20cinematic%20composition%2C%20soft%20natural%20textures%2C%20ethereal%20atmosphere&width=1800&height=1000&seq=hero-main&orientation=landscape";

export default function Home() {
  const latestNews = articles
    .filter((a) => a.category === "news")
    .slice(0, 3);

  const awardArticles = articles
    .filter((a) => a.category === "awards")
    .slice(0, 2);

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "苍井优中文资讯站",
    url: "https://yuaoi-fans.com",
    description:
      "全面介绍苍井优的演艺生涯、代表作品与个人魅力。了解这位日本实力派女演员的精彩世界。",
    inLanguage: "zh-CN",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://yuaoi-fans.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "苍井优中文资讯站",
    url: "https://yuaoi-fans.com",
    description:
      "专注于介绍日本女演员苍井优的演艺生涯与作品的资讯平台",
    foundingDate: "2020",
    contactPoint: {
      "@type": "ContactPoint",
      email: "contact@yuaoi-fans.com",
      contactType: "customer service",
    },
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "苍井优——演技与魅力并存的日本实力女演员",
    description:
      "苍井优，日本著名女演员，以精湛演技和独特气质征服无数观众。从舞台剧到电影电视，苍井优凭借多部经典作品荣获权威大奖。",
    url: "https://yuaoi-fans.com/",
    isPartOf: {
      "@type": "WebSite",
      name: "苍井优中文资讯站",
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(orgSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pageSchema),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src={heroBg}
              alt="苍井优"
              title="苍井优——演技与魅力并存的日本实力女演员"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full px-6 md:px-10 text-center">
                <h1
                  className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  苍井优
                </h1>
                <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-6 leading-relaxed">
                  日本著名女演员，以精湛演技和独特气质征服无数观众。从舞台剧到电影电视，苍井优凭借多部经典作品荣获权威大奖，成为日本影视界最具代表性的实力派女星之一。
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link
                    to="/category/film"
                    className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    探索作品
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white border border-white/30 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-50">
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-4 md:p-5 bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Works */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
            <div className="mb-8 flex items-end justify-between">
              <div>
                <h2
                  className="text-2xl md:text-3xl font-bold text-foreground-950"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  精选作品
                </h2>
                <p className="text-sm text-foreground-500 mt-2">
                  苍井优最具代表性的影视与舞台作品
                </p>
              </div>
              <Link
                to="/category/film"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.slice(0, 4).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>

          {/* Awards & Recognition */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-50">
            <div className="mb-8">
              <h2
                className="text-2xl md:text-3xl font-bold text-foreground-950"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                获奖荣誉
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                苍井优演艺生涯中的重要奖项与认可
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {awardArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col sm:flex-row gap-4 p-5 bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300/50 transition-all duration-300"
                >
                  <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.imageAlt}
                      title={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-award-line text-sm"></i>
                      </span>
                      <span className="text-xs text-foreground-400">
                        {article.date}
                      </span>
                    </div>
                    <h3
                      className="text-base font-semibold text-foreground-950 mb-1.5 group-hover:text-primary-500 transition-colors line-clamp-2"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Latest News */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
            <div className="mb-8 flex items-end justify-between">
              <div>
                <h2
                  className="text-2xl md:text-3xl font-bold text-foreground-950"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  最新动态
                </h2>
                <p className="text-sm text-foreground-500 mt-2">
                  苍井优的最新消息与活动资讯
                </p>
              </div>
              <Link
                to="/category/news"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestNews.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>

          {/* Biography Teaser */}
          <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-50">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="w-full lg:w-1/2">
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Japanese%20actress%20Yu%20Aoi%20natural%20portrait%20in%20warm%20sunlight%2C%20minimalist%20composition%2C%20soft%20elegant%20atmosphere%2C%20artistic%20photography%2C%20warm%20earthy%20tones%2C%20clean%20natural%20background&width=800&height=600&seq=bio-teaser&orientation=landscape"
                    alt="苍井优个人资料"
                    title="苍井优个人资料"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <h2
                  className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  从福冈走向世界的实力派女演员
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  苍井优，1985年8月17日出生于日本福冈县。从小学开始接触表演，到成为日本影视界最具代表性的实力派女演员之一。她擅长用极其细腻的方式诠释角色的内心世界，被誉为"用眼神演戏的女演员"。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  无论是天真烂漫的少女、坚韧不拔的女性，还是复杂多面的角色，苍井优都能够精准地捕捉到角色的灵魂所在。她的表演风格独树一帜——以克制为底色，在不动声色间释放出惊人的情感力量。
                </p>
                <Link
                  to="/category/biography"
                  className="inline-flex items-center gap-2 bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  了解更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}